// Map preview images: the map's levelshot from its pk3 (JPG or TGA), the
// command map image (_cc), or a thumbnail the game captured the first time the
// map was played. Results are cached as blob URLs.

import { storedThumbs } from '../../fs/storage';
import type { VirtualFS } from '../../fs/vfs';
import { decodeTga } from '../../render/tga';

/** Files tried, in order. */
export function levelshotCandidates(map: string): string[] {
  const m = map.toLowerCase();
  return [`levelshots/${m}.jpg`, `levelshots/${m}.tga`, `levelshots/${m}_cc.jpg`, `levelshots/${m}_cc.tga`];
}

/** The first levelshot file present for a map, or null. */
export function findLevelshot(fs: Pick<VirtualFS, 'has'>, map: string): string | null {
  return levelshotCandidates(map).find((p) => fs.has(p)) ?? null;
}

const cache = new Map<string, Promise<string | null>>();

/** Forgets cached images (after pk3s were added or removed, or a new thumbnail). */
export function clearLevelshotCache(map?: string): void {
  for (const [key, url] of cache) {
    if (map && key !== map.toLowerCase()) continue;
    url.then((u) => u && URL.revokeObjectURL(u));
    cache.delete(key);
  }
}

/** Blob URL of the best preview image of a map, or null when there is none. */
export function levelshotUrl(vfs: VirtualFS, map: string): Promise<string | null> {
  const key = map.toLowerCase();
  let p = cache.get(key);
  if (!p) {
    p = loadLevelshot(vfs, key).catch((err) => {
      console.warn(`levelshot ${map}:`, err);
      return null;
    });
    cache.set(key, p);
  }
  return p;
}

async function loadLevelshot(vfs: VirtualFS, map: string): Promise<string | null> {
  const path = findLevelshot(vfs, map);
  if (path) {
    const data = await vfs.read(path);
    if (data) {
      if (path.endsWith('.jpg')) return URL.createObjectURL(new Blob([data as Uint8Array<ArrayBuffer>], { type: 'image/jpeg' }));
      const blob = await tgaToBlob(data);
      if (blob) return URL.createObjectURL(blob);
    }
  }
  const thumb = await storedThumbs.load(map).catch(() => undefined);
  return thumb ? URL.createObjectURL(thumb) : null;
}

async function tgaToBlob(data: Uint8Array): Promise<Blob | null> {
  const img = decodeTga(data);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const pixels = new ImageData(img.width, img.height);
  pixels.data.set(img.data);
  // levelshots are opaque; some are saved with an empty alpha channel
  for (let i = 3; i < pixels.data.length; i += 4) pixels.data[i] = 255;
  ctx.putImageData(pixels, 0, 0);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9));
}

/** Scales a frame of the game canvas down to a 480x270 JPEG thumbnail. */
export function captureThumbnail(source: HTMLCanvasElement): Promise<Blob | null> {
  const W = 480;
  const H = 270;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx || !source.width || !source.height) return Promise.resolve(null);
  // cover: crop the source to 16:9
  const scale = Math.max(W / source.width, H / source.height);
  const sw = W / scale;
  const sh = H / scale;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, (source.width - sw) / 2, (source.height - sh) / 2, sw, sh, 0, 0, W, H);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85));
}
